import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase-config';

@customElement('auth-modal')
export class AuthModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @state() private isLogin = true;
  @state() private email = '';
  @state() private password = '';
  @state() private loading = false;
  @state() private error = '';

  createRenderRoot() {
    return this;
  }

  render() {
    if (!this.open) return html``;

    return html`
      <div class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div class="glass-effect cyber-border rounded-2xl p-8 w-full max-w-md relative animate-slide-up">
          <!-- Close Button -->
          <button class="absolute top-4 right-4 w-10 h-10 rounded-full glass-effect border border-gray-600/50 hover:border-primary/60 text-gray-400 hover:text-white transition-all duration-300 transform hover:scale-110 flex items-center justify-center" @click=${this.close}>
            <i class="fas fa-times"></i>
          </button>

          <!-- Header -->
          <div class="text-center mb-8">
            <div class="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center animate-glow">
              <i class="fas fa-shield-alt text-dark-card text-2xl"></i>
            </div>
            <h2 class="text-3xl font-gaming font-bold mb-2 neon-text">
              ${this.isLogin ? 'ELITE ACCESS' : 'JOIN ELITE'}
            </h2>
            <p class="text-gray-400">${this.isLogin ? 'Enter the arena' : 'Become a warrior'}</p>
          </div>

          <!-- Google Sign In Button -->
          <button
            @click=${this.handleGoogleSignIn}
            class="group w-full py-4 px-6 bg-white hover:bg-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 mb-6 flex items-center justify-center gap-3 border-2 border-transparent hover:border-primary/30"
            ?disabled=${this.loading}
          >
            <svg class="w-6 h-6 transition-transform group-hover:scale-110" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            ${this.loading ? 'Authenticating...' : 'Continue with Google'}
          </button>

          <div class="flex items-center mb-6">
            <div class="flex-1 border-t border-primary/20"></div>
            <span class="px-4 text-gray-400 text-sm font-medium">OR ENTER MANUALLY</span>
            <div class="flex-1 border-t border-primary/20"></div>
          </div>

          <form @submit=${this.handleSubmit} class="space-y-6">
            <div>
              <label for="email" class="block text-gray-300 text-sm font-medium mb-3 flex items-center">
                <i class="fas fa-envelope mr-2 text-primary"></i>Elite Email
              </label>
              <input
                type="email"
                id="email"
                class="w-full px-4 py-3 border border-gray-600/50 rounded-xl bg-dark-card/50 text-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                placeholder="warrior@elite.com"
                .value=${this.email}
                @input=${(e: any) => this.email = e.target.value}
                required
              />
            </div>

            <div>
              <label for="password" class="block text-gray-300 text-sm font-medium mb-3 flex items-center">
                <i class="fas fa-lock mr-2 text-primary"></i>Security Code
              </label>
              <input
                type="password"
                id="password"
                class="w-full px-4 py-3 border border-gray-600/50 rounded-xl bg-dark-card/50 text-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                placeholder="Enter your elite password"
                .value=${this.password}
                @input=${(e: any) => this.password = e.target.value}
                required
              />
            </div>

            ${this.error ? html`
              <div class="glass-effect border border-red-500/30 bg-red-500/10 text-red-400 text-sm p-3 rounded-xl flex items-center">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                ${this.error}
              </div>
            ` : ''}

            <button
              type="submit"
              class="group w-full py-4 px-6 cyber-border bg-gradient-to-r from-primary/20 to-accent/20 hover:from-primary/30 hover:to-accent/30 disabled:from-gray-600/20 disabled:to-gray-600/20 disabled:cursor-not-allowed text-white font-gaming font-bold rounded-xl transition-all duration-300 transform hover:scale-105 animate-glow"
              ?disabled=${this.loading}
            >
              <span class="relative z-10 flex items-center justify-center">
                <i class="fas fa-shield-alt mr-3"></i>
                ${this.loading ? 'ACCESSING...' : (this.isLogin ? 'ENTER ARENA' : 'JOIN ELITE')}
              </span>
            </button>
          </form>

          <!-- Toggle Mode -->
          <div class="text-center mt-8 p-4 glass-effect rounded-xl">
            <span class="text-gray-400">
              ${this.isLogin ? 'New warrior?' : 'Elite member?'}
            </span>
            <button @click=${this.toggleMode} class="text-primary hover:text-accent ml-2 font-semibold transition-colors duration-300">
              ${this.isLogin ? 'Join the Elite' : 'Access Arena'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private toggleMode() {
    this.isLogin = !this.isLogin;
    this.error = '';
  }

  private close() {
    this.open = false;
    this.error = '';
  }

  private async handleGoogleSignIn() {
    this.loading = true;
    this.error = '';

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      this.close();
      window.location.href = '/tournaments';
    } catch (error: any) {
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    this.loading = true;
    this.error = '';

    try {
      if (this.isLogin) {
        await signInWithEmailAndPassword(auth, this.email, this.password);
      } else {
        await createUserWithEmailAndPassword(auth, this.email, this.password);
      }
      this.close();
      window.location.href = '/tournaments';
    } catch (error: any) {
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  }

  show() {
    this.open = true;
  }
}
